import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpenIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarIcon,
  ClockIcon,
  ExchangeIcon,
  MessageCircleIcon
} from './ProfileIcons';

export interface Conversation {
  id: string;
  date: Date | string;
  duration: string;
  exchanges: number;
}

export interface BookConversations {
  bookTitle: string;
  bookAuthor: string;
  bookSlug: string;
  bookCoverUrl?: string;
  conversations: Conversation[];
}

interface BookGroupProps {
  book: BookConversations;
  isDarkMode: boolean;
  isPopup?: boolean;
}

const EXPANDED_PREVIEW_LIMIT = 3;
const ITEMS_PER_PAGE = 15;

export const BookGroup: React.FC<BookGroupProps> = ({ book, isDarkMode, isPopup = false }) => {
  const [isOpen, setIsOpen] = useState(isPopup);
  const [showAllConversations, setShowAllConversations] = useState(isPopup);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(book.conversations.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    const options: Intl.DateTimeFormatOptions = {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    const formatted = d.toLocaleDateString("en-US", options);
    return formatted.replace(/, (\d)/, " at $1");
  };

  const getLastConversationTime = () => {
    if (book.conversations.length === 0) return "";

    const lastConversation = book.conversations[0];
    const date = new Date(lastConversation.date);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) {
      return `${diffSeconds} ${diffSeconds === 1 ? 'second' : 'seconds'} ago`;
    } else if (diffMinutes < 60) {
      return `${diffMinutes} ${diffMinutes === 1 ? 'min' : 'mins'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else if (diffWeeks < 4) {
      return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
    } else if (diffMonths < 12) {
      return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
    } else {
      return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
    }
  };

  const handleToggle = () => {
    if (isPopup) return;
    setIsOpen(!isOpen);
    if (isOpen) {
      setShowAllConversations(false);
      setCurrentPage(1);
    }
  };

  const handleShowMoreConversations = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAllConversations(!showAllConversations);
    if (showAllConversations) {
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    pages.push(1);

    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      endPage = 4;
    }

    if (currentPage >= totalPages - 2) {
      startPage = totalPages - 3;
    }

    if (startPage > 2) {
      pages.push("ellipsis");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages - 1) {
      pages.push("ellipsis");
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const themeClasses = isDarkMode
    ? 'bg-gray-800/50 text-white border-gray-700'
    : 'bg-gray-50 text-gray-900 border-gray-300';

  const hoverClasses = isDarkMode
    ? 'hover:bg-gray-800/65'
    : 'hover:bg-gray-50';

  const secondaryTextClasses = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const badgeClasses = isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-950';
  const iconClasses = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  // Get current page conversations
  const currentConversations = book.conversations.slice(startIndex, endIndex);

  return (
    <div className={`rounded-xl border overflow-hidden transition-all duration-200 ${themeClasses} ${!isPopup ? 'hover:shadow-lg' : 'flex flex-col h-full'}`}>
      {/* Book Header - Clickable to toggle */}
      <div
        onClick={handleToggle}
        className={`w-full p-6 pr-5 flex items-center gap-5 transition-colors ${!isPopup ? 'cursor-pointer' : 'shrink-0 z-10'} ${hoverClasses}`}
      >
        {/* Book Cover */}
        <div className={`shrink-0 h-20 w-20 rounded-md shadow-sm flex items-center justify-center overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
          {book.bookCoverUrl ? (
            <img
              src={book.bookCoverUrl}
              alt={book.bookTitle}
              className="w-full h-full object-cover object-bottom"
              onError={(e) => {
                // Fallback if image fails to load
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).parentElement?.classList.add('flex');
              }}
            />
          ) : (
            <BookOpenIcon className={`h-8 w-8 ${iconClasses}`} />
          )}
        </div>

        {/* Book Info */}
        <div className="flex-1 text-left min-w-0">
          <h3 className={`portrait-text-app text-base xs:text-lg md:text-xl font-medium mb-1 line-clamp-2 sm:line-clamp-1`}>
            {book.bookTitle}
          </h3>
          <p className={`text-xs sm:text-sm mb-1 sm:mb-2 line-clamp-1 ${secondaryTextClasses}`}>
            by {book.bookAuthor}
          </p>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badgeClasses}`}>
            {book.conversations.length} {book.conversations.length === 1 ? "conversation" : "conversations"}
          </span>
        </div>

        {/* Last conversation time */}
        {book.conversations.length > 0 && (
          <div className={`hidden sm:block shrink-0 ${isPopup ? 'mr-1' : ''}`}>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClasses}`}>
              Last: {getLastConversationTime()}
            </span>
          </div>
        )}

        {/* Expand/Collapse Icon */}
        {!isPopup && (
          <div className="shrink-0">
            {isOpen ? (
              <ChevronUpIcon className={`h-7 w-7 ${iconClasses}`} />
            ) : (
              <ChevronDownIcon className={`h-7 w-7 ${iconClasses}`} />
            )}
          </div>
        )}
      </div>

      {/* Conversations List */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={!isPopup ? { height: 0, opacity: 0 } : false}
            animate={{ height: isPopup ? '100%' : 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className={`border-t flex flex-col min-h-0 ${isPopup ? 'flex-1' : 'overflow-hidden'} ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}
          >
            <div className={`flex flex-col min-h-0 ${isPopup ? 'flex-1' : ''}`}>
              {/* Scrollable Content Area */}
              <div className={isPopup ? 'flex-1 overflow-y-auto custom-scrollbar' : ''}>
                <div className="flex flex-col">
                  {currentConversations.map((conversation, index) => (
                    <div
                      key={conversation.id}
                      className={`group px-4 sm:px-6 py-4 flex flex-col sm:flex-row gap-3 transition-all duration-200 ${hoverClasses} ${
                        index !== 0 ? `border-t ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200'}` : ""
                      }`}
                    >
                      {/* Left side - Date and Time stacked */}
                      <div className="flex-1 min-w-0 flex flex-col gap-2 sm:gap-1">
                        <div className="flex items-center gap-2 text-sm">
                          <CalendarIcon className={`shrink-0 ${iconClasses}`} size={'sm'} />
                          <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {formatDate(conversation.date)}
                          </span>
                        </div>
                        <div className="flex flex-row sm:flex-col gap-6 sm:gap-1">
                          <div className={`flex items-center gap-2 text-sm ${secondaryTextClasses}`}>
                            <ClockIcon className="shrink-0" size={'sm'} />
                            <span className="tabular-nums">{conversation.duration}</span>
                          </div>

                          {/* Mobile Exchanges */}
                          <div className={`flex sm:hidden items-center gap-2 text-sm ${secondaryTextClasses}`}>
                            <ExchangeIcon className="shrink-0" size={'sm'} />
                            <span>{conversation.exchanges} {conversation.exchanges === 1 ? 'exchange' : 'exchanges'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Desktop Exchanges */}
                      <div className={`hidden sm:flex items-center gap-2 text-sm sm:ml-auto ${secondaryTextClasses}`}>
                        <ExchangeIcon className="shrink-0" size={'sm'} />
                        <span>{conversation.exchanges} {conversation.exchanges === 1 ? 'exchange' : 'exchanges'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fixed Footer Area (Show More/Less and Pagination) */}
              <div className="flex flex-col shrink-0">
                {/* Show More/Less Conversations Button */}
                {!isPopup && book.conversations.length > EXPANDED_PREVIEW_LIMIT && (
                  <button
                    onClick={handleShowMoreConversations}
                    className={`w-full px-6 py-4 flex items-center justify-center border-t text-sm font-medium transition-colors cursor-pointer shrink-0 ${
                      isDarkMode
                        ? 'bg-gray-800/50 hover:bg-gray-700/25 border-gray-700 text-gray-300 hover:text-gray-200'
                        : 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-600 hover:text-gray-700'
                    }`}
                  >
                    {showAllConversations ? (
                      <>
                        <ChevronUpIcon className="mr-2 h-5 w-5" />
                        Show less
                      </>
                    ) : (
                      <>
                        <ChevronDownIcon className="mr-2 h-5 w-5" />
                        Show more conversations
                      </>
                    )}
                  </button>
                )}

                {/* Pagination */}
                {showAllConversations && totalPages > 1 && (
                  <div className={`px-6 py-4 shrink-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={(e) => handlePageChange(currentPage - 1, e)}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-md ${
                          currentPage === 1
                            ? 'cursor-default opacity-50'
                            : 'cursor-pointer'
                        } ${
                          isDarkMode
                            ? `text-gray-400 ${currentPage !== 1 ? 'hover:text-white' : ''}`
                            : `text-gray-500 ${currentPage !== 1 ? 'hover:text-gray-900' : ''}`
                        }`}
                      >
                        <ChevronUpIcon className="h-5 w-5 -rotate-90" />
                        <span className="sr-only">Previous</span>
                      </button>

                      {getPageNumbers().map((page, idx) => (
                        <React.Fragment key={idx}>
                          {page === "ellipsis" ? (
                            <span className={`px-2 ${secondaryTextClasses}`}>...</span>
                          ) : (
                            <button
                              onClick={(e) => handlePageChange(page as number, e)}
                              className={`min-w-8 h-8 flex items-center justify-center rounded-md text-sm font-medium transition-colors ${
                                currentPage === page
                                  ? `cursor-default ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'}`
                                  : `cursor-pointer ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700/50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`
                              }`}
                            >
                              {page}
                            </button>
                          )}
                        </React.Fragment>
                      ))}

                      <button
                        onClick={(e) => handlePageChange(currentPage + 1, e)}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-md ${
                          currentPage === totalPages
                            ? 'cursor-default opacity-50'
                            : 'cursor-pointer'
                        } ${
                          isDarkMode
                            ? `text-gray-400 ${currentPage !== totalPages ? 'hover:text-white' : ''}`
                            : `text-gray-500 ${currentPage !== totalPages ? 'hover:text-gray-900' : ''}`
                        }`}
                      >
                        <ChevronDownIcon className="h-5 w-5 -rotate-90" />
                        <span className="sr-only">Next</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
